import {
  CANCEL_MEDIA_GENERATION_JOB_MUTATION,
  CHAMPIONSHIP_MEDIA_SUBJECTS_QUERY,
  ChampionshipMediaSubjects,
  DISCARD_MEDIA_GENERATION_JOB_MUTATION,
  GENERATE_MEDIA_MUTATION,
  GenerateMediaInput,
  MEDIA_GENERATION_JOBS_QUERY,
  MediaGenerationJob,
  MediaGenerationJobsInput,
  MediaGenerationJobsPage,
  RETRY_MEDIA_GENERATION_JOB_MUTATION,
} from 'modules/manager/domain/graphql/media-generation-jobs';
import { isKnownMediaKind, MediaKind } from 'modules/manager/domain/media-kind';
import { baseApi } from 'shared/api/base-api';

export const MEDIA_GENERATION_JOB_TAG = 'MediaGenerationJob' as const;

interface ChampionshipSubjectsGql {
  championship: {
    id: number;
    name: string;
    stages: {
      id: number;
      name: string;
      rounds: {
        id: number;
        name: string;
        matches: {
          id: number;
          date: string | null;
          teams: { id: number; name: string }[];
          round: { id: number; name: string } | null;
        }[];
      }[];
      teamGroups: { id: number; name: string }[];
    }[];
  };
}

function mapJob(raw: MediaGenerationJob): MediaGenerationJob {
  return {
    ...raw,
    kind: isKnownMediaKind(raw.kind) ? raw.kind : MediaKind.MATCH_ANNOUNCE,
  };
}

function mapSubjects(raw: ChampionshipSubjectsGql['championship']): ChampionshipMediaSubjects {
  const matches: ChampionshipMediaSubjects['matches'] = [];
  const rounds: ChampionshipMediaSubjects['rounds'] = [];
  const groups: ChampionshipMediaSubjects['groups'] = [];
  const stages = raw.stages.map((stage) => {
    stage.rounds.forEach((round) => {
      rounds.push({ id: round.id, name: round.name });
      round.matches.forEach((match) => matches.push(match));
    });
    stage.teamGroups.forEach((group) => groups.push(group));
    return { id: stage.id, name: stage.name };
  });

  return {
    id: raw.id,
    name: raw.name,
    matches,
    rounds,
    stages,
    groups,
  };
}

export const mediaGenerationJobsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    mediaGenerationJobs: build.query<MediaGenerationJobsPage, MediaGenerationJobsInput>({
      query: (input) => ({
        query: MEDIA_GENERATION_JOBS_QUERY,
        variables: { input },
      }),
      transformResponse: (raw: { mediaGenerationJobs: MediaGenerationJobsPage }) => ({
        ...raw.mediaGenerationJobs,
        items: raw.mediaGenerationJobs.items.map(mapJob),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((job) => ({ type: MEDIA_GENERATION_JOB_TAG, id: job.id })),
              { type: MEDIA_GENERATION_JOB_TAG, id: 'LIST' },
            ]
          : [{ type: MEDIA_GENERATION_JOB_TAG, id: 'LIST' }],
    }),
    championshipMediaSubjects: build.query<ChampionshipMediaSubjects, number>({
      query: (id) => ({
        query: CHAMPIONSHIP_MEDIA_SUBJECTS_QUERY,
        variables: { id },
      }),
      transformResponse: (raw: ChampionshipSubjectsGql) => mapSubjects(raw.championship),
    }),
    generateMedia: build.mutation<MediaGenerationJob, GenerateMediaInput>({
      query: (input) => ({
        query: GENERATE_MEDIA_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { generateMedia: MediaGenerationJob }) => mapJob(raw.generateMedia),
      invalidatesTags: [{ type: MEDIA_GENERATION_JOB_TAG, id: 'LIST' }],
    }),
    discardMediaGenerationJob: build.mutation<{ status: string }, number>({
      query: (jobId) => ({
        query: DISCARD_MEDIA_GENERATION_JOB_MUTATION,
        variables: { jobId },
      }),
      transformResponse: (raw: { discardMediaGenerationJob: { status: string } }) => raw.discardMediaGenerationJob,
      invalidatesTags: [{ type: MEDIA_GENERATION_JOB_TAG, id: 'LIST' }],
    }),
    retryMediaGenerationJob: build.mutation<MediaGenerationJob, number>({
      query: (jobId) => ({
        query: RETRY_MEDIA_GENERATION_JOB_MUTATION,
        variables: { jobId },
      }),
      transformResponse: (raw: { retryMediaGenerationJob: MediaGenerationJob }) => mapJob(raw.retryMediaGenerationJob),
      invalidatesTags: [{ type: MEDIA_GENERATION_JOB_TAG, id: 'LIST' }],
    }),
    cancelMediaGenerationJob: build.mutation<{ status: string }, number>({
      query: (jobId) => ({
        query: CANCEL_MEDIA_GENERATION_JOB_MUTATION,
        variables: { jobId },
      }),
      transformResponse: (raw: { cancelMediaGenerationJob: { status: string } }) => raw.cancelMediaGenerationJob,
      invalidatesTags: [{ type: MEDIA_GENERATION_JOB_TAG, id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMediaGenerationJobsQuery,
  useChampionshipMediaSubjectsQuery,
  useGenerateMediaMutation,
  useDiscardMediaGenerationJobMutation,
  useRetryMediaGenerationJobMutation,
  useCancelMediaGenerationJobMutation,
} = mediaGenerationJobsApi;
